import type { TableView } from "owa-mail-list-store";
import MailListItemSelectionSource from "owa-mail-store/lib/store/schema/MailListItemSelectionSource";
import { mutatorAction } from "satcheljs";
import endSelectMailItemDatapoint from "owa-mail-logging/lib/utils/endSelectMailItemDatapoint";
import shouldOpenPopout from "../../utils/shouldOpenPopout";
import shouldOpenReadingPaneInSingleLineView from "../../utils/shouldOpenReadingPaneInSingleLineView";
import resetExpansionViewStateInternal from "owa-mail-list-store/lib/utils/resetExpansionViewStateInternal";
import clearSelection from "owa-mail-list-store/lib/utils/clearSelection";
import { getStore as getMessageAdListStore } from "owa-mail-messagead-list-store";
import { isImmersiveReadingPaneShown } from "owa-mail-layout";

/**
 * Single select mail row
 * @param tableView where the operation is being performed
 * @param rowKey the rowKey of the item to select
 * @param mailListItemSelectionSource The source of selection on mail item
 * @param headerToFocus The header to focus instead of the row
 */
const singleSelectRowInternalMutatorActionMutator: (tableView: TableView, rowKey: string, mailListItemSelectionSource: MailListItemSelectionSource, headerToFocus: string | null) => void = mutatorAction("singleSelectRowInternalMutatorAction", function singleSelectRowInternalMutatorAction(tableView: TableView, rowKey: string, mailListItemSelectionSource: MailListItemSelectionSource, headerToFocus: string | null): void {
    // popout scenario not supported in this mutatorAction
    if (shouldOpenPopout(mailListItemSelectionSource)) {
        return;
    }
    
    const isSingleRowSelected = tableView.selectedRowKeys.has(rowKey) && tableView.selectedRowKeys?.size === 1;
    
    // Simply log and retrun if user is selecting the same item and that item is focused unless
    // user has reading pane setting as hidden (single line view) AND the reading pane should be opened.
    if (isSingleRowSelected &&
        rowKey === tableView.focusedRowKey &&
        tableView.headerToFocus === headerToFocus &&
        !shouldOpenReadingPaneInSingleLineView(mailListItemSelectionSource)) {
        // End the SelectMailItem related datapoint here and invalidate since reading pane
        // is already loaded.
        endSelectMailItemDatapoint("" /* contentType */, true /* shouldInvalidate */);
        return;
    }
    
    // If this rowKey is the only selected row and its twisty is being toggled, there is no need to reset and set anything new.
    if (!(mailListItemSelectionSource == MailListItemSelectionSource.MailListItemTwisty &&
        isSingleRowSelected)) {
        // Reset list view selection
        clearSelection(tableView);
        tableView.isInCheckedMode = false;
        tableView.isOnlyOptimisticallyInCheckedMode = false;
        resetExpansionViewStateInternal();
        // Reset the message Ad list selection which potentially occupy the right pane
        const messageAdStore = getMessageAdListStore();
        if (messageAdStore) {
            messageAdStore.selectedAdId = "";
        }
        
        tableView.isInVirtualSelectAllMode = false;
        // Skip resetting already-empty observable arrays to avoid triggering unnecessary MobX reactions
        if (!tableView.virtualSelectAllExclusionList ||
            tableView.virtualSelectAllExclusionList.length > 0) {
            tableView.virtualSelectAllExclusionList = [];
        }
        tableView.selectAllModeTimeStamp = null;
        
        if (headerToFocus && !isImmersiveReadingPaneShown()) {
            // Clear focused row key when focusing on header
            tableView.focusedRowKey = null;
            tableView.lastAutoFocusedRowKey = null;
        }
        else {
            tableView.focusedRowKey = rowKey; // Set focused row key to the selected
            tableView.selectedRowKeys.set(rowKey, true);
        }
        
        // Set the selection anchor for possible future range, keyboard, or multi selections
        tableView.selectionAnchorRowKey = rowKey;
        tableView.multiSelectionAnchorRowKey = null;
        
        tableView.headerToFocus = headerToFocus;
    }
});
export default singleSelectRowInternalMutatorActionMutator;
